import {i18n} from '@online-offer/i18n';
import {Separator} from 'platform/components';
import {Heading, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {any, isNotNil, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {MoneyResponseBody} from '../../api/types/money';
import {CardOo} from '../../components/CardOo/CardOo';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ReservationRow} from './ReservationRow';

type ReservationCardType = {
  isExpired?: boolean;
};

export function ReservationCard(_props: ReservationCardType) {
  const {
    upsell,
    totalPriceWithVat,
    totalPriceWithoutVat,
    totalDiscountPriceWithVat,
    totalDiscountPriceWithoutVat,
    vehiclePriceWithVat,
    vehiclePriceWithoutVat,
    originalCarPriceWithVat,
    originalCarPriceWithoutVat,
  } = useGetOnlineOfferData();

  const formatCurrency = useFormatCurrency();

  const formatPrice = (price: MoneyResponseBody | Nullish) =>
    price?.amount ? formatCurrency(parseFloat(price.amount), price.currency) : null;

  const isDiscountNonZero = (discount: MoneyResponseBody | Nullish) =>
    Number(discount?.amount ?? 0) > 0;

  if (
    !any(
      isNotNilOrEmpty,
      values([
        upsell,
        totalPriceWithVat,
        totalPriceWithoutVat,
        totalDiscountPriceWithVat,
        totalDiscountPriceWithoutVat,
        vehiclePriceWithVat,
        vehiclePriceWithoutVat,
        originalCarPriceWithVat,
        originalCarPriceWithoutVat,
      ])
    )
  ) {
    return null;
  }

  return (
    <CardOo backgroundColor="semantic.bg.subtle">
      <VStack width="100%" spacing={5}>
        <VStack spacing={2}>
          <Show when={isNotNil(formatPrice(totalPriceWithVat))}>
            <ReservationRow
              label={i18n.t('reservationCard.totalPrice')}
              value={<Heading size={3}>{formatPrice(totalPriceWithVat)}</Heading>}
              labelProps={{alternative: true}}
            />
          </Show>
          <ReservationRow
            label={i18n.t('reservationCard.totalPriceWithoutVat')}
            value={formatPrice(totalPriceWithoutVat)}
          />
        </VStack>
        {/* TODO: uncomment when ready on BE(no task yet) */}
        {/*<VStack spacing={2}>*/}
        {/*  <Button*/}
        {/*    leftIcon="good"*/}
        {/*    title="Mám zájem o vůz"*/}
        {/*    data-testid="reservationCard-resrve"*/}
        {/*    variant="primary"*/}
        {/*    onClick={() => {}}*/}
        {/*  />*/}
        {/*  <Button*/}
        {/*    leftIcon={props.isExpired ? 'specification/year' : 'navigation/cancel'}*/}
        {/*    leftIconSize={4}*/}
        {/*    title={props.isExpired ? 'Rezervovat vůz' : 'Zrušit rezervaci'}*/}
        {/*    data-testid="reservationCard-cancel"*/}
        {/*    variant="inverted"*/}
        {/*    onClick={() => {}}*/}
        {/*  />*/}
        {/*</VStack>*/}
        <Separator spacing={0} />
        <VStack spacing={2}>
          <Show when={isNotNil(formatPrice(vehiclePriceWithVat))}>
            <ReservationRow
              label={i18n.t('reservationCard.carPrice')}
              labelProps={{alternative: true}}
              value={<Heading size={4}>{formatPrice(vehiclePriceWithVat)}</Heading>}
            />
          </Show>
          <ReservationRow
            label={i18n.t('reservationCard.carPriceWithoutVat')}
            value={formatPrice(vehiclePriceWithoutVat)}
          />
          <ReservationRow
            label={i18n.t('reservationCard.originalCarPrice')}
            value={formatPrice(originalCarPriceWithVat ?? originalCarPriceWithoutVat)}
            valueProps={{color: 'danger', strikethrough: true}}
          />
          <Show
            when={
              isDiscountNonZero(totalDiscountPriceWithVat) ||
              isDiscountNonZero(totalDiscountPriceWithoutVat)
            }
          >
            <ReservationRow
              label={i18n.t('reservationCard.customerDiscount')}
              value={formatPrice(totalDiscountPriceWithVat ?? totalDiscountPriceWithoutVat)}
              valueProps={{color: 'success'}}
            />
          </Show>
        </VStack>
        <Show when={upsell && upsell.length > 0}>
          <Separator spacing={0} />
          <VStack spacing={2}>
            <ReservationRow
              label={i18n.t('reservationCard.upsell')}
              labelProps={{alternative: true}}
            />
            {upsell?.map((singleItem) => {
              if (
                isNilOrEmpty(singleItem.name) ||
                (isNilOrEmpty(singleItem.sellingPrice?.withVat) &&
                  isNilOrEmpty(singleItem.sellingPrice?.withoutVat))
              ) {
                return null;
              }
              return (
                <ReservationRow
                  key={singleItem.id}
                  label={singleItem.name!}
                  value={formatPrice(
                    singleItem.sellingPrice?.withVat ?? singleItem.sellingPrice?.withoutVat
                  )}
                />
              );
            })}
          </VStack>
        </Show>
        {/* TODO: financovanie - not planned in online-offer v1 */}
        {/*<CardOo backgroundColor="general.white" radius="small">*/}
        {/*  <ReservationRow*/}
        {/*    label="Při financování od"*/}
        {/*    labelProps={{alternative: true}}*/}
        {/*    value={<Heading size={4}>26 786 Kč/měs</Heading>}*/}
        {/*  />*/}
        {/*  <Separator spacing={2} />*/}
        {/*  <ReservationRow label="Při financování od" value="60 měsíců (5 let)" />*/}
        {/*</CardOo>*/}
      </VStack>
    </CardOo>
  );
}
